import type { MoodWeek } from "../../../types/gpSummary";
import Card from "./Card";

interface MoodTrendsCardProps {
  mood: MoodWeek[];
  avg: string;
  trendText: string;
}

function MoodBar({ score, week, isLast }: { score: number; week: string; isLast: boolean }) {
  const pct = (score / 5) * 100;
  return (
    <div className="flex flex-col items-center gap-1 flex-1">
      <span className={`text-xs font-semibold ${isLast ? "text-[#6C7A52]" : "text-stone-400"}`}>
        {score}
      </span>
      <div className="w-full flex items-end" style={{ height: 56 }}>
        <div
          className="w-full rounded-t-md"
          style={{
            height: `${pct}%`,
            minHeight: 6,
            background: isLast
              ? "linear-gradient(180deg,#6C7A52,#C6D8A5)"
              : "linear-gradient(180deg,#C6D8A5,#e2edcf)",
          }}
        />
      </div>
      <span className="text-xs text-stone-400 font-medium">{week}</span>
    </div>
  );
}

export default function MoodTrendsCard({ mood, avg, trendText }: MoodTrendsCardProps) {
  if (mood.length === 0) {
    return (
      <Card title="Mood Trends">
        <p className="text-sm text-stone-400 italic">No mood data recorded yet.</p>
      </Card>
    );
  }

  return (
    <Card title="Mood Trends">
      <div className="flex flex-col lg:flex-row gap-5">
        {/* Bars */}
        <div className="flex-1 rounded-xl p-4 bg-stone-50">
          <div className="flex items-end gap-2 mb-3">
            {mood.map((d, i) => (
              <MoodBar key={d.week} {...d} isLast={i === mood.length - 1} />
            ))}
          </div>
          <div className="flex justify-between items-center pt-3 border-t border-stone-200">
            <div>
              <span className="font-bold text-stone-800 text-2xl">{avg}</span>
              <span className="text-sm text-stone-400 ml-1">/ 5 avg</span>
            </div>
            <span
              className="text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{ background: "#e9f0dc", color: "#6C7A52" }}
            >
              {mood.length >= 2 && mood[mood.length - 1].score > mood[0].score
                ? "\u2191 Improving"
                : mood.length >= 2 && mood[mood.length - 1].score < mood[0].score
                  ? "\u2193 Declining"
                  : "\u2192 Stable"}
            </span>
          </div>
        </div>
        {/* Breakdown list */}
        <div className="flex-1 flex flex-col justify-center gap-2">
          {mood.map((d, i) => (
            <div key={d.week} className="flex items-center gap-3">
              <span className="text-xs text-stone-400 font-medium w-6">{d.week}</span>
              <div className="flex-1 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(d.score / 5) * 100}%`,
                    background: i === mood.length - 1
                      ? "linear-gradient(90deg,#6C7A52,#C6D8A5)"
                      : "linear-gradient(90deg,#C6D8A5,#e2edcf)",
                  }}
                />
              </div>
              <span className="text-xs font-bold text-stone-700 w-6">{d.score}</span>
              <span
                className="text-xs font-medium px-2 py-0.5 rounded-full text-center"
                style={{
                  minWidth: 56,
                  background: d.label === "Good" || d.label === "Great"
                    ? "#e9f0dc"
                    : d.label === "Average"
                      ? "#f0f6e4"
                      : "#F5E8E8",
                  color: d.label === "Good" || d.label === "Great"
                    ? "#6C7A52"
                    : d.label === "Average"
                      ? "#8a9f60"
                      : "#C97C7C",
                }}
              >
                {d.label}
              </span>
            </div>
          ))}
          <div
            className="mt-3 text-xs leading-relaxed p-3 rounded-r-lg"
            style={{ borderLeft: "3px solid #C6D8A5", background: "#f0f6e4", color: "#2C2C2C" }}
          >
            {trendText}
          </div>
        </div>
      </div>
    </Card>
  );
}
